import { Injectable } from '@angular/core';
import { AuthService } from '@service/auth.service';
import { UtilService } from '@service/util.service';
import * as UtilFunc from '@helper/util.helper';
import { APP_CONSTANTS } from '@app/_configs/constant';

interface IAddress{
    street: string,
    city: string,
    suburb: string,
    postcode: string,
    state: string,
    country: any
}

export interface IBanner{
    image: string,
    title: string,
    subtitle: string,
    button: string,
    href: string
}

export interface IService{
    name?: string,
    description: string,
    icon: string
}

export interface IAbout{
    image: string,
    title: string,
    subtitle: string,
    button: string,
    href: string
}

export interface ITestimonial{
    name: string,
    text: string,
    rating: number
}

export interface ITestimonialsSection {
    description: string,
    items: ITestimonial[]
}

export interface IDiscovery{
    title: string,
    content: string,
    image1: string,
    image2: string
}

export interface IExperienceItem{
    title: string,
    content: string,
    image: string
}

export interface IExperience{
    title: string,
    content: string,
    items: IExperienceItem[]
}

export interface ISignature{
    title: string,
    content: string,
    image: string | null
}

export interface IPromotion{
    title: string,
    content: string,
    image: string | null
}

export interface ICoupon{
    title: string,
    content: string,
    start_date: string | null,
    end_date: string | null,
    disabled: boolean
}

export interface ICateringPackage {
    title: string;
    items: string[];
    image?: string;
}

export interface ICateringSection {
    title?: string;
    subtitle?: string;
    description?: string;
    image?: string;
    intro_image?: string;
    intro_title?: string;
    intro_description?: string;
    cta_image?: string;
    cta_button?: string;
    cta_href?: string;
    packages?: ICateringPackage[];
}

interface ICustomerPointGift{
    code: string,
    label: string
}

export const DEFAULT_MENU_VIEW_THEME = {
    colors: {
        primary: '#B4292C',
        deep: '#7A1E1F',
        page_bg: '#FBF7F1',
        header_bg: '#FFFFFF',
        card: '#FFFFFF',
        border: '#ECDFCF',
        text_primary: '#1A1A1A',
        text_muted: '#6B6B6B',
        open_now: '#2F9D5F',
    },
    fonts: {
        heading: 'Playfair Display',
        body: 'Rubik',
    },
    sizes: {
        store_name: 34,
        tagline: 11,
        status: 13,
        heading: 28,
        item_count: 13,
        item_name: 16,
        item_desc: 13,
        price: 16,
        category: 13,
        button: 13,
        cart: 14,
        order_title: 22,
        order_total: 32,
    },
    layout: {
        card_layout: 'image_top',
        card_radius: 16,
        image_height: 200,
        columns: 3,
    }
};

@Injectable({
    providedIn: 'root'
})
export class Store {
    _id: string = '';
    private_web_address: string = '';
    reservation: boolean = false;
    store_name: string = '';
    domain_name: string = '';
    /** Domain has multiple storefronts; list loaded separately for picker. */
    is_multiple_location: boolean = false;
    first_name: string = '';
    last_name: string = '';
    email: string = '';
    phone: string = '';
    profile_image: string = '';
    logo: string = '';
    social_link: {
        facebook: string,
        twitter: string,
        linkedin: string,
        youtube: string,
        instagram: string,
        pinterest: string,
        tiktok: string,
        google: string,
        google_maps: string,
        [key: string]: string
    }
    cateringLabel: string = 'CATERING';
    blogLabel: string = 'BLOG';
    faqLabel: string = 'REVIEW';
    website: string = '';
    physical_address: IAddress;
    postal_address: IAddress;
    sequence_number: number = 0;
    default_currency: any = null;
    default_tax: string = '';
    template: string = '';
    user_switch_security: number = 1;
    show_map_on_frontend: boolean = false;
    active:boolean = false;
    click_collect: boolean = false;
    plan: {
        id:string,
        subscriptionId:string
    };
    renewal_date:string = '';
    store_pickup: boolean = true;
    store_delivery: boolean = true;
    moneris:{
        active: boolean,
        moneris_store_id: string,
        moneris_api_key: string
    }
    globalpayments:{
        active: boolean,
        app_id: string,
        app_key: string,
        environment: string
    }
    paypal:{
        active: boolean,
        secret: string,
        client_id: string
    }
    stripe:{
        active: boolean,
        secret_key: string,
        public_key: string
    }
    preferences: {
        messagebox: boolean,
        confirm_delete_product: boolean,
        confirm_discard_sale: boolean,
        confirm_pay: boolean
    }
    active_widget: {
        services: boolean,
        sliders: boolean,
        banners: boolean,
        about_us: boolean,
        faq: boolean,
        blog: boolean,
        catering: boolean,
        only_menu_view: boolean,
    }
    sliders: IBanner[] = [];
    mobile_sliders: IBanner[] = [];
    banners: IBanner[] = [];
    services: IService[] = [];
    abouts: IAbout[] = [];
    testimonials: ITestimonialsSection = { description: '', items: [] };
    discovery: IDiscovery;
    experience: IExperience;
    signature: ISignature;
    promotion: IPromotion;
    coupon: ICoupon;
    catering: ICateringSection;
    hero: {
        title1: string,
        title2: string,
        background: string,
        description: string,
    };
    footer: {
        physical_address: string,
        phone: string,
        email: string,
    };
    short_description:string = '';
    theme_color: string = '';
    menu_view_theme: any;
    private _storeLoaded = false;
    private _storeLoadInFlight = false;
    private _storeLoadWaiters: Array<{success?: Function, no_exist?: Function}> = [];
    customer_point_gift:string[] = [];
    gift_rate: number = 0;
    dealer_rate: number = 0;
    store_hours: any;

    static CUSTOMER_POINT_GIFT:ICustomerPointGift[] = [
        {code: 'point_used', label: 'Point Used'},
        {code: 'complex_point_used', label: 'Complex Point Used'},
        {code: 'special_items_excluded_on_point', label: 'Special Items excluded on point'},
        {code: 'on_issuing_new_card_customer_information_gathering_form_printed', label: 'On issuing new card, customer information gathering form printed'},
        {code: 'point_amount_displayed_as_number_instead_of_money_amount', label: 'Point amount displayed as number instead of money amount'},
        {code: 'point_amount_applied_to_subtotal', label: 'Point amount applied to subtotal'},
        {code: 'point_information_is_not_printed_on_receipt', label: 'Point Information is not printed on receipt'},
        {code: 'only_total_point_printed_on_receipt', label: 'Only total point printed on receipt (No point history)'},
        {code: 'gift_card_usage_history_is_not_printed_on_receipt', label: 'Gift card usage history is not printed on receipt'},
        {code: 'point_amount_displayed_on_Sale_window', label: 'Point amount displayed on Sale window'},
        {code: 'gift_bonus_added', label: 'Gift Bonus added'},
        {code: 'auto_custid_used', label: 'Auto custID used'},
        {code: 'dr_used', label: 'DR used'},
        {code: 'customer_card_9_digits', label: 'Customer Card: 9 digits'},
        {code: 'phone_number_can_not_used_as_card_number', label: 'Phone number can not used as card number'},
        {code: 'dealer_point_used', label: 'Dealer Point Used'}
    ]
    customer_point_gift_settings:any= {};
    user:any;
    util = UtilFunc;

    constructor(
		private authService: AuthService,
		private utilService: UtilService)	{	
        
		this.user = this.authService.getCurrentUser;		

        for(let s of Store.CUSTOMER_POINT_GIFT) {
            this.customer_point_gift_settings[s.code] = false;
        }

		this.init();
	}

    init() {
        this._id = '';
        this.private_web_address = '';
        this.store_name = '';
        this.reservation = false;
        this.domain_name = '';
        this.is_multiple_location = false;
        this.first_name = '';
        this.last_name = '';
        this.email = '';
        this.phone = '';
        this.profile_image = '';
        this.logo = '';
        this.social_link = {            
            facebook: '',
            twitter: '',
            linkedin: '',
            youtube: '',
            instagram: '',
            pinterest: '',
            tiktok: '',
            google: '',
            google_maps: ''
        };
        this.website = '';
        this.physical_address = {
            street: '',
            city: '',
            suburb: '',
            postcode: '',
            state: '',
            country: null
        };
        this.postal_address = {
            street: '',
            city: '',
            suburb: '',
            postcode: '',
            state: '',
            country: null
        };
        this.sequence_number = 0;
        this.default_currency = null;
        this.default_tax = 'outlet';
        this.template = '';
        this.user_switch_security = 1;        
        this.active = false;
        this.click_collect = false;
        this.store_pickup = true;
        this.show_map_on_frontend = false;
        this.store_delivery=true;
        this.moneris = {
            active: false,
            moneris_store_id: '',
            moneris_api_key: ''
        };
        this.globalpayments = {
            active: false,
            app_id: '',
            app_key: '',
            environment: 'sandbox'
        }
        this.paypal = {
            active: false,
            secret: '',
            client_id: ''
        };
        this.stripe = {
            active: false,
            secret_key: '',
            public_key: ''
        }    
        this.plan = {
            id: 'free',
            subscriptionId: ''
        };
        this.preferences = {
            messagebox : true,
            confirm_delete_product: true,
            confirm_discard_sale: true,
            confirm_pay: true
        },
        this.sliders = [];
        this.mobile_sliders = [];
        this.store_hours = [];
        this.banners = [];
        this.services = [];
        this.hero = {            
            title1: '',
            title2: '',
            background: '',
            description: ''
        };
        this.abouts = [];
        this.testimonials = { description: '', items: [] };
        this.discovery = {
            title: '',
            content: '',
            image1: '',
            image2: ''
        };
        this.experience = {
            title: '',
            content: '',
            items: []
        };
        this.signature = {
            title: '',
            content: '',
            image: null
        };
        this.promotion = {
            title: '',
            content: '',
            image: null
        };
        this.coupon = {
            title: '',
            content: '',
            start_date: null,
            end_date: null,
            disabled: false
        };
        this.catering = {
            title: '',
            subtitle: '',
            description: '',
            image: '',
            intro_image: '',
            intro_title: '',
            intro_description: '',
            cta_image: '',
            cta_button: '',
            cta_href: '',
            packages: []
        };
        this.footer = {            
            email: '',
            phone: '',
            physical_address: '',
        };
        this.short_description = '';
        this.theme_color = '';
        this.menu_view_theme = JSON.parse(JSON.stringify(DEFAULT_MENU_VIEW_THEME));
        this.active_widget = {
            sliders: true,
            banners: true,
            services: true,
            about_us: true,
            faq: false,
            blog: false,
            catering: false,
            only_menu_view: false
        };
        this.customer_point_gift = [];
        this.gift_rate = 0;
        this.dealer_rate = 0;
    }

    load(success?:Function, no_exist?:Function) {
        if (this._storeLoaded && this._id) {
            if (success) success();
            return;
        }
        this._storeLoadWaiters.push({ success, no_exist });
        if (this._storeLoadInFlight) {
            return;
        }
        this._storeLoadInFlight = true;
        let params: any = {};
        
        // Get store identifier from URL
        if (APP_CONSTANTS.IS_FRONT) {
            const domain = this.util.getDomain();
            let savedPwa = '';
            try {
                const raw = localStorage.getItem(APP_CONSTANTS.STOREFRONT_DOMAIN_STORE_SELECTION_KEY);
                if (raw) {
                    const map = JSON.parse(raw);
                    savedPwa = (map?.[domain] || '').toString().trim();
                }
            } catch {
                savedPwa = '';
            }
            const pwa = (savedPwa || this.utilService.private_web_address || '').toString().trim();
            if (pwa) {
                params.domain_name = domain;
                params.private_web_address = pwa;
            } else {
                params.domain_name = domain;
            }
        } else {
            const private_web_address = this.util.getPrivateWebAddress();
            if (private_web_address) {
                params.private_web_address = private_web_address;
            }
        }
        
        this.utilService.get('auth/store', params).subscribe(result => {   			
            this._storeLoadInFlight = false;
            if(result && result.body) {
                const store = result.body;
                this._id = store._id;
                this.private_web_address = store.private_web_address;
                this.domain_name = store.domain_name || '';
                this.is_multiple_location = !!(
                    store.is_multiple_location ||
                    store.is_multi_location ||
                    store.is_mulitple_location
                );
                this.store_name = store.store_name || '';
                this.reservation = store.reservation || false;
                this.first_name = store.first_name || '';
                this.last_name = store.last_name || '';
                this.email = store.email || '';
                this.phone = store.phone || '';
                this.profile_image = store.profile_image || '';
                this.show_map_on_frontend = store.show_map_on_frontent || false;
                this.logo = store.logo || '';
                this.cateringLabel = store.cateringLabel?.trim() || 'CATERING';
                this.blogLabel = store.blogLabel?.trim() || 'BLOG';
                this.faqLabel = store.faqLabel?.trim() || 'REVIEW';
                this.social_link = {
                    facebook: '',
                    twitter: '',
                    linkedin: '',
                    youtube: '',
                    instagram: '',
                    pinterest: '',
                    tiktok: '',
                    google: '',
                    google_maps: '',
                    ...(store.social_link || {})
                };
                this.website = store.website || '';
                this.physical_address = store.physical_address;
                this.postal_address = store.postal_address;
                this.sequence_number = store.sequence_number || 0;
                this.default_currency = store.default_currency;
                this.default_tax = store.default_tax || 'outlet';
                this.template = store.template || '';
                this.user_switch_security = store.user_switch_security || 1;
                this.preferences = store.preferences;
                this.active = store.active;
                this.click_collect = store.click_collect || false;
                this.store_pickup = store.store_pickup;
                this.store_delivery = store.store_delivery;
                this.moneris = store.moneris || {active: false, moneris_store_id: '', moneris_api_key: ''}; 
                this.globalpayments = store.globalpayments || {active: false, app_id: '', app_key: '', environment: 'sandbox'}; 
                this.paypal = store.paypal || {active: false, secret: '', client_id: ''}; 
                this.stripe = store.stripe || {active: false, secret_key: '', public_key: ''}; 
                this.sliders = store.sliders || [];
                this.mobile_sliders = store.mobile_sliders || [];
                this.banners = store.banners || [];
                this.services = store.services || [];
                this.hero = store.hero || {};
                this.abouts = store.abouts || [];
                this.testimonials = store.testimonials || { description: '', items: [] };
                this.discovery = store.discovery || {
                    title: '',
                    content: '',
                    image1: '',
                    image2: ''
                };
                this.experience = store.experience || {
                    title: '',
                    content: '',
                    items: []
                };
                this.signature = store.signature || {
                    title: '',
                    content: '',
                    image: null
                };
                this.promotion = store.promotion || {
                    title: '',
                    content: '',
                    image: null
                };
                this.coupon = store.coupon || {
                    title: '',
                    content: '',
                    start_date: null,
                    end_date: null,
                    disabled: false
                };
                this.catering = store.catering || {
                    title: '',
                    subtitle: '',
                    description: '',
                    image: '',
                    intro_image: '',
                    intro_title: '',
                    intro_description: '',
                    cta_image: '',
                    cta_button: '',
                    cta_href: '',
                    packages: []
                };
                this.footer = store.footer || {};
                this.short_description = store.short_description || '';
                this.theme_color = store.theme_color || '';
                this.menu_view_theme = this.mergeMenuViewTheme(store.menu_view_theme);
                this.active_widget = {
                    sliders: true,
                    banners: true,
                    services: true,
                    about_us: true,
                    faq: false,
                    blog: false,
                    catering: false,
                    only_menu_view: false,
                    ...(store.active_widget || {})
                };
                if(store.plan) this.plan = store.plan;
                this.customer_point_gift = store.customer_point_gift || [];
                this.gift_rate = store.gift_rate;
                this.store_hours = store.store_hours;
                this.dealer_rate = store.dealer_rate;
                for(let s of Store.CUSTOMER_POINT_GIFT) {
                    let index = this.customer_point_gift.findIndex(item => item == s.code);
                    if(index > -1) {
                        this.customer_point_gift_settings[s.code] = true;
                    }
                }
                this.applyMenuViewTheme();
                this._storeLoaded = true;
                this.flushStoreLoadWaiters(true);
            } else {
                this.flushStoreLoadWaiters(false);
            }
        }, error => {
            this._storeLoadInFlight = false;
            this.flushStoreLoadWaiters(false);
        });
    }

    private flushStoreLoadWaiters(ok: boolean): void {
        const waiters = this._storeLoadWaiters.splice(0);
        waiters.forEach(w => {
            if (ok) {
                if (w.success) w.success();
            } else if (w.no_exist) {
                w.no_exist();
            }
        });
    }

    loadData(data:any) {
        Object.keys(data).forEach(key => {
            this[key] = data[key];
        });      
        this.active_widget = {
            sliders: true,
            banners: true,
            services: true,
            about_us: true,
            faq: false,
            blog: false,
            catering: false,
            only_menu_view: false,
            ...(this.active_widget || {})
        };
        this.menu_view_theme = this.mergeMenuViewTheme(this.menu_view_theme);
        this.applyMenuViewTheme();
        if (this._id) {
            this._storeLoaded = true;
        }
    }

    public get data():any {
        let customer_point_gift:string[] = [];
        for(let s of Store.CUSTOMER_POINT_GIFT) {
            if(this.customer_point_gift_settings[s.code]) {
                if(s.code == 'complex_point_used' && !this.customer_point_gift_settings.point_used) continue;
                customer_point_gift.push(s.code);
            }
        }
        this.customer_point_gift = customer_point_gift;
        let data = {
            _id: this._id,
            private_web_address: this.private_web_address,
            store_name: this.store_name,
            reservation: this.reservation,
            domain_name: this.domain_name,
            first_name: this.first_name,
            last_name: this.last_name,
            email: this.email,
            phone: this.phone,
            profile_image: this.profile_image,
            logo: this.logo,
            social_link: this.social_link,
            show_map_on_frontend: this.show_map_on_frontend,
            website: this.website,
            physical_address: {
                street: this.physical_address.street,
                city: this.physical_address.city,
                suburb: this.physical_address.suburb,
                postcode: this.physical_address.postcode,
                state: this.physical_address.state,
                country: this.physical_address.country ? this.physical_address.country._id: null
            },
            postal_address: {
                street: this.postal_address.street,
                city: this.postal_address.city,
                suburb: this.postal_address.suburb,
                postcode: this.postal_address.postcode,
                state: this.postal_address.state,
                country: this.postal_address.country ? this.postal_address.country._id: null
            },
            sequence_number : this.sequence_number,
            default_currency: this.default_currency ? this.default_currency._id: null,
            default_tax: this.default_tax,
            template: this.template,
            user_switch_security: this.user_switch_security,
            preferences: this.preferences,
            active: this.active,
            click_collect: this.click_collect,
            store_pickup: this.store_pickup,
            store_delivery: this.store_delivery,
            moneris: {...this.moneris},
            globalpayments: {...this.globalpayments},
            paypal: {...this.paypal},
            stripe: {...this.stripe},
            plan: this.plan,
            banners: this.banners,
            sliders: this.sliders,
            mobile_sliders: this.mobile_sliders,
            services: this.services,
            hero: this.hero,
            abouts: this.abouts,
            testimonials: this.testimonials,
            discovery: this.discovery,
            experience: this.experience,
            signature: this.signature,
            promotion: this.promotion,
            coupon: this.coupon,
            footer: this.footer,
            short_description: this.short_description,
            theme_color: this.theme_color,
            menu_view_theme: this.menu_view_theme,
            active_widget: this.active_widget,
            customer_point_gift: this.customer_point_gift,
            gift_rate: this.gift_rate,
            dealer_rate: this.dealer_rate
        };        
        return data;
    }

    public get isOnlyMenuView(): boolean {
        return !!this.active_widget?.only_menu_view;
    }

    mergeMenuViewTheme(theme: any) {
        const defaults = JSON.parse(JSON.stringify(DEFAULT_MENU_VIEW_THEME));
        if (!theme || typeof theme !== 'object') {
            return defaults;
        }
        return {
            colors: { ...defaults.colors, ...(theme.colors || {}) },
            fonts: { ...defaults.fonts, ...(theme.fonts || {}) },
            sizes: { ...defaults.sizes, ...(theme.sizes || {}) },
            layout: { ...defaults.layout, ...(theme.layout || {}) },
        };
    }

    applyMenuViewTheme(): void {
        const root = document.documentElement;
        if (!this.isOnlyMenuView) {
            document.body.classList.remove('only-menu-view');
            document.body.classList.remove('mv-card-left');
            document.body.style.backgroundColor = '';
            return;
        }
        document.body.classList.add('only-menu-view');
        const theme = this.mergeMenuViewTheme(this.menu_view_theme);
        const colors = theme.colors;
        document.body.style.backgroundColor = colors.page_bg;
        const fonts = theme.fonts;
        const sizes = theme.sizes;
        root.style.setProperty('--mv-primary', colors.primary);
        root.style.setProperty('--mv-deep', colors.deep);
        root.style.setProperty('--mv-page-bg', colors.page_bg);
        root.style.setProperty('--mv-header-bg', colors.header_bg);
        root.style.setProperty('--mv-card', colors.card);
        root.style.setProperty('--mv-border', colors.border);
        root.style.setProperty('--mv-text', colors.text_primary);
        root.style.setProperty('--mv-muted', colors.text_muted);
        root.style.setProperty('--mv-open', colors.open_now);
        root.style.setProperty('--mv-font-heading', fonts.heading);
        root.style.setProperty('--mv-font-body', fonts.body);
        root.style.setProperty('--mv-size-store-name', sizes.store_name + 'px');
        root.style.setProperty('--mv-size-tagline', sizes.tagline + 'px');
        root.style.setProperty('--mv-size-status', sizes.status + 'px');
        root.style.setProperty('--mv-size-heading', sizes.heading + 'px');
        root.style.setProperty('--mv-size-item-count', sizes.item_count + 'px');
        root.style.setProperty('--mv-size-item-name', sizes.item_name + 'px');
        root.style.setProperty('--mv-size-item-desc', sizes.item_desc + 'px');
        root.style.setProperty('--mv-size-price', sizes.price + 'px');
        root.style.setProperty('--mv-size-category', sizes.category + 'px');
        root.style.setProperty('--mv-size-button', sizes.button + 'px');
        root.style.setProperty('--mv-size-cart', sizes.cart + 'px');
        root.style.setProperty('--mv-size-order-title', sizes.order_title + 'px');
        root.style.setProperty('--mv-size-order-total', (sizes.order_total || sizes.heading || 32) + 'px');
        const layout = theme.layout || {};
        const radius = Number(layout.card_radius);
        root.style.setProperty('--mv-card-radius', (isNaN(radius) ? 16 : radius) + 'px');
        root.style.setProperty('--mv-card-image-height', (Number(layout.image_height) || 200) + 'px');
        const columns = Number(layout.columns) || 3;
        root.style.setProperty('--mv-card-columns', String(Math.min(3, Math.max(1, columns))));
        document.body.classList.toggle('mv-card-left', layout.card_layout === 'image_left');
    }

    public get menuViewCardLayout(): string {
        return this.menu_view_theme?.layout?.card_layout === 'image_left' ? 'image_left' : 'image_top';
    }

    /**
     * Returns true if the store coupon info is valid and should be shown:
     * enabled (not disabled), has title or content, and current date is within start_date/end_date (if set).
     */
    isCouponInfoValid(): boolean {
        const c = this.coupon;
        if (!c || c.disabled) return false;
        const hasContent = (c.title && c.title.trim()) || (c.content && c.content.trim());
        if (!hasContent) return false;
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (c.start_date) {
            const start = new Date(c.start_date);
            if (isNaN(start.getTime())) return false;
            start.setHours(0, 0, 0, 0);
            if (now < start) return false;
        }
        if (c.end_date) {
            const end = new Date(c.end_date);
            if (isNaN(end.getTime())) return false;
            end.setHours(23, 59, 59, 999);
            if (now > end) return false;
        }
        return true;
    }

    save(callback?:Function, error?:Function) {
        const data = this.data;
        if(data._id) {
            this.utilService.put('auth/store', data).subscribe(result => {
                if(callback) callback(result);
            }, err => {
                if(error) error();
            })
        } else {
            delete data._id;
            this.utilService.post('auth/store', data).subscribe(result => {
                this._id = result.body.result._id;
                if(callback) callback(result);
            }, err => {
                if(error) error();
            })
        }
    }
}
